/**
 * PaginationControls — numbered-pagination footer for dense admin / HR
 * tables. Pairs with the `usePagedList` hook from `lib/use-paged-list`.
 *
 * Renders (left → right, wrapping on narrow viewports):
 *   1. A record summary: "Showing X–Y of Z · Page P of T" (or "No records").
 *   2. A rows-per-page selector (10 / 20 / 50 / 100 by default).
 *   3. Prev / numbered pages (with ellipsis) / Next.
 *
 * Purely presentational — the hook owns page/pageSize state. Every control
 * is a native <button> / <select>, so keyboard (Tab, Enter/Space, and the
 * select's native arrow handling) works for free; the active page carries
 * `aria-current="page"`. Hit targets are ≥ 34 px; focus rings are visible.
 *
 * Visual model: inactive pages are ghost (transparent, fill on hover); the
 * active page is a filled brand chip; Prev/Next are chevron ghost buttons
 * that mute when disabled. Hover/focus states need real CSS, so a small
 * scoped stylesheet is injected once (idempotent).
 *
 * The numbered-page window stays compact: first + last are always shown,
 * plus the current page ±1, with an ellipsis token bridging any gap — so a
 * 200-page list never paints 200 buttons.
 */

type PageToken = number | 'ellipsis-left' | 'ellipsis-right'

interface PaginationControlsProps {
  page: number
  pageSize: number
  total: number
  totalPages: number
  pageSizeOptions: readonly number[]
  /** 1-based index of the first visible row (0 when empty). */
  rangeStart: number
  /** 1-based index of the last visible row (0 when empty). */
  rangeEnd: number
  canPrev: boolean
  canNext: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  /** True while a fetch is in flight — dims the bar to signal "updating"
   *  without hiding the current page. Default false. */
  isFetching?: boolean
  /** Standalone mode for surfaces that are NOT a single table-in-a-card
   *  (e.g. a grid or list of individual cards — HR Blackouts, Manager team).
   *  Drops the card-footer chrome (top divider, surface fill, bottom radius)
   *  so the bar reads as a plain controls row on the page rather than a
   *  detached card fragment. Default false (attached-footer inside a card). */
  standalone?: boolean
}

// ── Scoped styles (hover / focus / active — not expressible inline) ──────────

const STYLE_ID = 'pgn-controls-styles'

function ensureStyles(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
    .pgn-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      min-width: 34px;
      height: 34px;
      padding: 0 9px;
      border: 1px solid transparent;
      border-radius: 9px;
      background: transparent;
      color: var(--text-2);
      font-size: 13px;
      font-weight: 500;
      font-variant-numeric: tabular-nums;
      cursor: pointer;
      transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
    }
    .pgn-btn:hover:not(:disabled):not(.pgn-btn--active) {
      background: var(--surface-2);
      color: var(--text);
    }
    .pgn-btn:focus-visible {
      outline: 2px solid var(--steel-blue);
      outline-offset: 2px;
    }
    .pgn-btn--active {
      background: var(--surface-2);
      border-color: var(--steel-blue);
      color: var(--steel-blue);
      font-weight: 700;
      cursor: default;
    }
    .pgn-btn--nav { padding: 0 11px; }
    .pgn-btn:disabled { color: var(--text-3); opacity: 0.5; cursor: default; }
    .pgn-size {
      height: 34px;
      padding: 0 30px 0 11px;
      border: 1px solid var(--border);
      border-radius: 9px;
      background: var(--surface);
      color: var(--text);
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      transition: border-color 120ms ease;
    }
    .pgn-size:hover { border-color: var(--border-strong, var(--text-3)); }
    .pgn-size:focus-visible { outline: 2px solid var(--steel-blue); outline-offset: 2px; }
    .pgn-ellipsis { min-width: 20px; text-align: center; color: var(--text-3); user-select: none; }
  `
  document.head.appendChild(style)
}

// ── Chevron ──────────────────────────────────────────────────────────────────

function Chevron({ dir }: { dir: 'left' | 'right' }): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path
        d={dir === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Compact page-number window: [1, …, p-1, p, p+1, …, N] with the ellipsis
 *  only where there's an actual gap. Deterministic and dependency-free. */
export function buildPageWindow(current: number, totalPages: number): PageToken[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  const tokens: PageToken[] = [1]
  const left = Math.max(2, current - 1)
  const right = Math.min(totalPages - 1, current + 1)

  if (left > 2) tokens.push('ellipsis-left')
  for (let p = left; p <= right; p++) tokens.push(p)
  if (right < totalPages - 1) tokens.push('ellipsis-right')

  tokens.push(totalPages)
  return tokens
}

export function PaginationControls({
  page,
  pageSize,
  total,
  totalPages,
  pageSizeOptions,
  rangeStart,
  rangeEnd,
  canPrev,
  canNext,
  onPageChange,
  onPageSizeChange,
  isFetching = false,
  standalone = false,
}: PaginationControlsProps): JSX.Element {
  ensureStyles()
  const tokens = buildPageWindow(page, totalPages)
  // A single page needs no navigation — Prev / a lone "1" / Next / "Page 1 of 1"
  // are all dead controls. Collapse them and keep just the count + rows picker.
  const showPageNav = totalPages > 1

  return (
    <nav
      data-testid="pagination-controls"
      aria-label="Pagination"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px 16px',
        padding: standalone ? '4px 2px' : '10px 14px',
        borderTop: standalone ? 'none' : '1px solid var(--french-gray-2)',
        background: standalone ? 'transparent' : 'var(--surface)',
        borderRadius: standalone ? 0 : '0 0 var(--radius-lg, 12px) var(--radius-lg, 12px)',
        opacity: isFetching ? 0.55 : 1,
        transition: 'opacity 120ms ease',
      }}
    >
      {/* ── Left: record summary + page-size selector ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span
          data-testid="pagination-summary"
          style={{ fontSize: 12.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}
        >
          {total === 0 ? (
            'No records'
          ) : (
            <>
              Showing{' '}
              <strong style={{ color: 'var(--text-2)', fontWeight: 600 }}>
                {rangeStart}–{rangeEnd}
              </strong>{' '}
              of{' '}
              <strong style={{ color: 'var(--text-2)', fontWeight: 600 }}>{total}</strong>
              {/* Page-of-total only when there's more than one page. */}
              {showPageNav ? (
                <span
                  data-testid="pagination-page-of"
                  style={{ color: 'var(--text-3)' }}
                >
                  {' · '}Page {page} of {totalPages}
                </span>
              ) : null}
            </>
          )}
        </span>

        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <label
            htmlFor="pgn-page-size"
            style={{ fontSize: 12.5, color: 'var(--text-3)', marginRight: 8 }}
          >
            Display
          </label>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <select
              id="pgn-page-size"
              className="pgn-size"
              data-testid="pagination-page-size"
              aria-label="Rows per page"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            {/* Custom caret (native one hidden via appearance:none) */}
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: 'var(--text-3)',
                display: 'inline-flex',
              }}
            >
              <Chevron dir="right" />
            </span>
          </span>
        </span>
      </div>

      {/* ── Right: prev / numbered pages / next (multi-page only) ── */}
      {showPageNav ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            type="button"
            className="pgn-btn pgn-btn--nav"
            data-testid="pagination-prev"
            aria-label="Previous page"
            disabled={!canPrev}
            onClick={() => onPageChange(page - 1)}
          >
            <Chevron dir="left" />
            Prev
          </button>

          {tokens.map((token) =>
            token === 'ellipsis-left' || token === 'ellipsis-right' ? (
              <span key={token} className="pgn-ellipsis" aria-hidden="true">
                …
              </span>
            ) : (
              <PageButton
                key={token}
                pageNumber={token}
                active={token === page}
                onClick={() => onPageChange(token)}
              />
            ),
          )}

          <button
            type="button"
            className="pgn-btn pgn-btn--nav"
            data-testid="pagination-next"
            aria-label="Next page"
            disabled={!canNext}
            onClick={() => onPageChange(page + 1)}
          >
            Next
            <Chevron dir="right" />
          </button>
        </div>
      ) : null}
    </nav>
  )
}

/** A single numbered page button. Active page reads as a filled chip and
 *  carries `aria-current="page"` so screen readers announce the location. */
function PageButton({
  pageNumber,
  active,
  onClick,
}: {
  pageNumber: number
  active: boolean
  onClick: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      className={active ? 'pgn-btn pgn-btn--active' : 'pgn-btn'}
      data-testid={`pagination-page-${pageNumber}`}
      aria-label={`Page ${pageNumber}`}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
    >
      {pageNumber}
    </button>
  )
}
